import { AppConfig } from '../../app.config';
import { AuthenticationService, authUserType, profileType, reRgType } from 'app/services/auth.service';
import { Component, OnInit, ViewChild, OnDestroy, ElementRef, Input, Output, EventEmitter, inject } from '@angular/core';
import {APIService} from 'app/services/api.service';
import { Subscription } from "rxjs";
import { NgClass, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

type formFileType = {
  hasRestrictedFileType: boolean;
  hasMaxFileUploadError: boolean;
  file: File;
};

type apiClientType = {
  DisplayName: string;
  NameId: string;
};

type apiRestrictedFileTypesType = {
  Id: string;
  Extension: string;
  Description: string;
};

type pgfRequestType = {
  charity: string;
  amount: string;
  message: string;
}

@Component({
    selector: 'nwm-advisor-contact',
    templateUrl: 'advisor-contact.component.html',
    styleUrls: ['advisor-contact.component.scss'],
    imports: [NgClass, FormsModule, NgbTooltip, CurrencyPipe]
})


export class AdvisorContactComponent implements OnInit, OnDestroy {
  private auth = inject(AuthenticationService);
  config = inject(AppConfig);
  private api = inject(APIService);

  @Input() formAction: 'MessageAdvisor' | 'SendDocuments' | 'PgfDonation' | 'PgfGrant' = 'MessageAdvisor';
  @Input() formTitle = 'Contact Advisory Team';
  // if isMFARequest = true, it means that the request is related to MFA & it's related to the user themselves
  @Input() isMFARequest = false;
  @Input() alreadySubmitted = false;
  @Input() defaultCharity = {charity: '', amount: '', message: ''};
  @Output() formSubmitted : EventEmitter<void> = new EventEmitter<void>();
  @Output() formCancelled : EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('attachFiles') attachFiles?: ElementRef;
  // in drag-drop area, the background is changed while dragging based on the value of isDragOver:
  isDragOver = false;
  restrictedFileTypes: apiRestrictedFileTypesType[] = [];
  selectedDocumentAction?: string = '0';
  contactMsg = '';
  formFiles: formFileType[] = [];
  hasMaxFileUploadError = true;
  hasRestrictedFileType = true;
  hasCombinedMaxFileUploadError = false;
  hasMessageError = false;
  hasDocRelateError = false;
  formError = false;
  hasFileUploadError = false;
  hasSubmitError = false
  hasDuplicatedFilesError = false;
  hasSelectClientError = false;

  pgfRequests:pgfRequestType[] = [{charity: '', amount: '', message: ''}];

  contact: { option: string; text: string; } = { option: '', text: '' };
  client: { phone: string; email: string; } = { phone: '', email: '' }
  document_actions: { id: null | string; value: string; }[] = [
    { "id": '0', "value": "--Please Select--" },
    { "id": "4", "value": "New account set-up" },
    { "id": "5", "value": "Existing investment account" },
    { "id": "8", "value": "Financial planning" },
    { "id": "7", "value": "Charitable giving" },
    { "id": "9", "value": "Other" },
  ];
  profile?: profileType;
  user?: authUserType;
  hasPreferredPhone = true;
  submitted = 0;
  isImpersonated = false;
  clientsList: { NameId: string; DisplayName: string; }[] = [];
  selectedClient = "";
  submittingTheForm = false;
  gettingClients = false;
  private subscription?: Subscription;
  public selectedReRg?: reRgType;

  ngOnInit() {
    this.selectedReRg = this.auth.getCurrentReRg()!;
    this.submitted = this.alreadySubmitted ? 1 : 0;
    this.hasMaxFileUploadError = false;
    this.hasRestrictedFileType = false;
    this.hasCombinedMaxFileUploadError = false;

    this.isImpersonated = !!sessionStorage.getItem('guid');
    if (this.defaultCharity.charity !== '') {
      this.pgfRequests = [this.defaultCharity];
    }

    this.user = this.auth.getUser();
    if (this.user!.isAccountant) {
      this.gettingClients = true;
      this.clientsList = [];
      this.api.get<apiClientType[]>(`${this.config.advisor.getLinkedClients}/${this.selectedReRg?.ReRgId}`).subscribe(
        data => {
          if (data) {
            data.forEach(c => this.clientsList.push({NameId:c.NameId, DisplayName:c.DisplayName.replace(/[\s,]+$/gm,'')}));
          }
          this.gettingClients = false;
        },
        () => this.gettingClients = false
      );
    }

    this.profile = this.auth.getProfile()!;
    this.client.email = this.profile.TwoFAEmail; // for email, 2fa email needs to be shown
    const phone = this.profile.PhoneNumber; // for phone-number, preferred phone number needs to be shown
    if (phone) {
      this.client.phone = phone;
    } else {
      this.client.phone = '<No Preferred Phone>';
      this.hasPreferredPhone = false;
    }
    this.contact.option = 'email';

    this.api.get<apiRestrictedFileTypesType[]>(this.config.advisor.restrictedFileTypesUrl).subscribe(
      types => {
        this.restrictedFileTypes = types;
      }
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  reset() {
    this.submitted = 0;
    this.formFiles = [];
    this.hasMaxFileUploadError = false;
    this.hasRestrictedFileType = false;
    this.hasCombinedMaxFileUploadError = false;
    this.hasMessageError = false;
    this.formError = false;
    this.hasDocRelateError = false;
    this.hasDuplicatedFilesError = false;
    this.hasSubmitError = false;
    this.hasSelectClientError = false;
    this.contactMsg = '';
    this.selectedDocumentAction = this.document_actions[0].id || undefined;
    this.contact.option = 'email';
    this.contact.text = '';
    this.selectedClient = '';
  }

  resetFormError() {
    this.hasMaxFileUploadError = false;
    this.hasRestrictedFileType = false;
    this.hasCombinedMaxFileUploadError = false;
    this.hasMessageError = false;
    this.formError = false;
    this.hasDocRelateError = false;
    this.hasFileUploadError = false;
    this.hasDuplicatedFilesError = false;
    this.hasSubmitError = false;
    this.hasSelectClientError = false;
  }

  formValidate() {
    this.formError = false;
    if (this.hasMaxFileUploadError || this.hasRestrictedFileType) {
      this.formError = true;
    }

    if ( this.formAction !== 'SendDocuments'  && this.contactMsg == '') {
      this.hasMessageError = true;
      this.formError = true;
    } else {
      this.hasMessageError = false;
    }

    if (this.user!.isAccountant && this.formAction === 'SendDocuments' && this.clientsList.length > 0 && !this.selectedClient) {
      document.getElementById('selectedClient')?.focus();
      this.hasSelectClientError = true;
      this.formError = true;
    } else {
      this.hasSelectClientError = false;
    }

    if (!this.user!.isAccountant && this.formAction === 'SendDocuments' && this.selectedDocumentAction == '0') {
      document.getElementById('selectedDocumentAction')?.focus();
      this.hasDocRelateError = true;
      this.formError = true;
    } else {
      this.hasDocRelateError = false;
    }

    if (this.formAction === 'SendDocuments' && this.formFiles.length == 0) {
      this.hasFileUploadError = true;
      this.formError = true;
    } else {
      this.hasFileUploadError = false;
    }
  }

  submitPgf() {
    this.formError = false;
    this.pgfRequests.forEach((f,i)=>{
      if (f.amount == '' || +f.amount<=0 || (this.formAction === 'PgfGrant' && +f.amount<500)) {
        this.formError = true;
        document.getElementById('amount_'+i)?.focus();
      }
      if (this.formAction === 'PgfGrant' && f.charity == '') {
        this.formError = true;
        document.getElementById('charity_'+i)?.focus();
      }
    });
    if (this.formError) return;

    let msg = '';
    if (this.formAction === 'PgfGrant') {
      this.pgfRequests.forEach((r,i)=>{
        msg+='<p class="msg">Grant Request'
          +(this.pgfRequests.length>1 ? ' #'+(i+1) : '')
          +': $'+r.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          +'<br>Charity: '+r.charity+'<br>'
          +r.message+'</p><br>';
      });
    } else {
      this.pgfRequests.forEach(r=>{
        msg+='<b>Amount:</b><br>$'+r.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")+'<br><br>';
        msg+='<b>Comments:</b><br>'+r.message+'<br><br>';
      });
    }

    const formData: FormData = new FormData();
    formData.append('subject', this.formAction === 'PgfGrant' ? '10' : '11' /*this.formTitle*/);
    formData.append('message', msg);
    formData.append('option', 'email');
    formData.append('reRgId', this.selectedReRg!.ReRgId?.toString() ?? '');

    this.submittingTheForm = true;
    this.api.sendMailWithAttachments(formData).subscribe({
      next: () => {
        this.submitted = 4;
        this.submittingTheForm = false;
        this.formSubmitted.emit();
      },
      error: () => {
        this.hasSubmitError = true;
        this.submittingTheForm = false;
      }
    });
  }

  submit() {
    this.hasSubmitError = false;
    this.formValidate();
    if (this.formError) return false;

    const formData: FormData = new FormData();

    const l = this.formFiles.length;
    if (l > 0) {
      for (let i=0; i<l; i++) {
        const formFile = this.formFiles[i];
        formData.append('uploadFile', formFile.file, formFile.file.name);
      }
    }
    formData.append('message', this.contactMsg);

    if(this.formAction === 'SendDocuments' && !this.selectedClient) {
      formData.append('subject', this.selectedDocumentAction as string);
    } else {
      formData.append('subject', this.formAction === 'SendDocuments' ? '3' : '1');
    }

    formData.append('option',  this.contact.option);
    formData.append('text',    this.contact.text);
    const reRgId = this.isMFARequest ? this.user?.nameId : this.selectedReRg!.ReRgId;
    formData.append('reRgId', reRgId?.toString() ?? '');

    if (this.formAction === 'SendDocuments' && this.selectedClient) {
      formData.append('reId', this.selectedClient);
      formData.append('isAccountantUpload', 'true');

      if (this.clientsList.length > 0) {
        const ids = this.clientsList.map(item => item.NameId);
        formData.append('clientsList', JSON.stringify(ids));
      }
    }

    this.submittingTheForm = true;
    this.api.sendMailWithAttachments(formData).subscribe({
      next: () => {
        this.submitted = parseInt(this.formAction === 'SendDocuments' ? '3' : '1');
        this.submittingTheForm = false;
        this.formSubmitted.emit();
      },
      error: () => {
        this.hasSubmitError = true;
        this.submittingTheForm = false;
      }
    });
    return true;
  }

  removeAttachment(i: number) {
    this.formFiles.splice(i,1);
    this.resetFormError();
  }

  fileChange(event: Event) {
    const files = ((event.target as HTMLInputElement).files as FileList);
    this.uploadFiles(files);
  }

  uploadFiles(files: FileList | undefined) {
    if (!files) {
      return;
    }
    const l = files.length;
    this.hasDuplicatedFilesError = false;
    if (l > 0) {
      for (let i=0; i<l; i++) {
        const file: File = files[i];
        if (this.formFiles.some(f => f.file.name === file.name)) {
          this.hasDuplicatedFilesError = true;
        } else {
          this.formFiles.push({
            file,
            hasRestrictedFileType: !this.isValidFileType(file.name),
            hasMaxFileUploadError: file.size > this.config.advisor.maxFileUploadSize,
          });
        }
      }
    }
    this.validateFiles();
    if (this.attachFiles) {
      this.attachFiles.nativeElement.value = null;
    }
    this.resetFormError();
  }

  validateFiles() {
    this.hasMaxFileUploadError = false;
    this.hasRestrictedFileType = false;
    let size = 0;
    this.formFiles.forEach(formFile => {
      if (formFile.hasRestrictedFileType) this.hasRestrictedFileType = true;
      if (formFile.hasMaxFileUploadError) this.hasMaxFileUploadError = true;
      size+=formFile.file.size;
    })
    this.hasCombinedMaxFileUploadError = size > this.config.advisor.maxFileUploadSize;
    if (size > 0) {
      this.hasFileUploadError = false;
    }
  }

  getFormat(fileName: string): string {
    const array = fileName.split('.');
    return array.length > 0 ? array[array.length - 1].toLowerCase() : '';
  }

  isValidFileType(filename: string): boolean {
    const format = this.getFormat(filename);
    if (format !== '' && this.restrictedFileTypes && this.restrictedFileTypes.length > 0) {

      for (let i = 0; i < this.restrictedFileTypes.length; i++) {
        if (format === this.restrictedFileTypes[i].Extension.replace('.','').toLowerCase()) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * it uploads files dropped into drag-drop area
   * @param event
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    this.uploadFiles(event?.dataTransfer?.files);
  }

  /**
   * it changes the value of isDragOver to change background during dragging
   * @param event
   */
  onDragOver(event: DragEvent): void {
    this.isDragOver = true;
    event.preventDefault();
  }

  /**
   * it changes the value of isDragOver to change background when dragging is finished
   * @param event
   */
  onDragLeave(event: DragEvent): void {
    this.isDragOver = false;
    event.preventDefault();
  }

  addPgfRequests() {
    this.pgfRequests.push({charity: '', amount: '', message: ''});
  }

}
